import React, { useState, useEffect } from 'react';

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import ProductSelect from '@shared/products/ProductSelect';
import { Form, InputNumber, Button, Space, Select, Input, message, Radio, Alert, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

interface InventoryTypeOption {
  id?: string;
  name?: string;
  code?: string;
  merchant_id?: string;
  inventory_type_id?: string;
  inventory_type?: { id?: string; name?: string; code?: string };
  [key: string]: unknown;
}

interface MerchantOption {
  value: string;
  label: string;
}

interface InventoryMonitorLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null } | null;
  [key: string]: unknown;
}

interface InventoryMonitorService {
  get: (id: string) => Promise<any>;
  create: (data: Record<string, unknown>) => Promise<any>;
  update: (id: string, data: Record<string, unknown>) => Promise<any>;
}

interface ProductServiceShape {
  [key: string]: unknown;
}

interface InventoryMonitorsEditProps {
  // Service interface (current usage everywhere). Kept as primary path; legacy
  // callbacks below are still accepted for backwards compatibility with any
  // historical callers.
  inventoryMonitorService?: InventoryMonitorService;
  productService: ProductServiceShape;
  loadInventoryMonitor?: (id: string) => Promise<any>;
  createInventoryMonitor?: (payload: Record<string, unknown>) => Promise<any>;
  updateInventoryMonitor?: (id: string, payload: Record<string, unknown>) => Promise<any>;
  // Reference data injected by wrapper — keeps the shared component
  // merchant-agnostic so wrapper can align it to the loaded record's merchant
  // (operator) or to the admin session merchant.
  inventoryTypes?: InventoryTypeOption[];
  inventoryMonitorId?: string;
  merchantOptions?: MerchantOption[];
  role?: 'admin' | 'operator';
  // Called by `loadData` once the monitor is fetched. Operator wrapper uses
  // this to sync OperatorDataContext.currentMerchant to the record's merchant
  // via useRecordMerchantSync before any merchant-scoped dropdown renders.
  onInventoryMonitorLoaded?: (monitor: InventoryMonitorLoadedPayload) => void;
  // True while the wrapper is still switching context to the record's merchant.
  // Render-gated to avoid the form briefly showing the previous merchant's options.
  merchantDataLoading?: boolean;
  // New-mode only: when admin (single-merchant session) or when operator has
  // exactly one accessible merchant, prefill form's merchant_id so create
  // payload carries it and ProductSelect / inventory_type filter both work.
  merchantIdForCreate?: string | null;
}

export default function InventoryMonitorsEdit({
  inventoryMonitorService,
  productService,
  loadInventoryMonitor,
  createInventoryMonitor,
  updateInventoryMonitor,
  inventoryTypes = [],
  inventoryMonitorId,
  merchantOptions = [],
  role = 'admin',
  onInventoryMonitorLoaded,
  merchantDataLoading = false,
  merchantIdForCreate = null,
}: InventoryMonitorsEditProps) {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 區分「儲存並新增下一筆」的 loading
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const isEditMode = !!inventoryMonitorId;
  // 新增模式 + 多商戶 + wrapper 未鎖定 merchant 時，顯示下拉讓使用者選。
  // 編輯模式不顯示（merchant 在 useRecordMerchantSync 同步後鎖在 record 的 merchant）。
  const showMerchantSelect = !isEditMode && !merchantIdForCreate && merchantOptions.length > 1;

  // 新增模式：當 wrapper 給了 merchantIdForCreate（admin / single-merchant operator），
  //         寫入 form 一次，後續由 user 在表單內互動。不再用 effect 監聽 context 變化
  //         重寫 form 值 —— 這正是舊版 bug 的根因（user 在 header 切 merchant 會
  //         覆蓋 edit-mode form 的 record merchant_id）。
  useEffect(() => {
    if (isEditMode) return;
    if (!merchantIdForCreate) return;
    const current = form.getFieldValue('merchant_id');
    if (!current) {
      form.setFieldsValue({ merchant_id: merchantIdForCreate });
    }
  }, [isEditMode, merchantIdForCreate, form]);

  // Watch form values
  const mechanism = Form.useWatch('mechanism', form);
  const merchantId = Form.useWatch('merchant_id', form);

  useEffect(() => {
    if (isEditMode) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryMonitorId]);

  const loadData = async () => {
    setLoading(true);
    try {
      let response;
      if (inventoryMonitorService) {
        response = await inventoryMonitorService.get(inventoryMonitorId!);
      } else if (loadInventoryMonitor) {
        response = await loadInventoryMonitor(inventoryMonitorId!);
      } else {
        throw new Error('No service or legacy loader provided');
      }

      if (response.success) {
        const data = response.data;
        // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
        // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
        // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
        onInventoryMonitorLoaded?.(data);
        form.setFieldsValue({
          merchant_id: data.merchant_id,
          product_id: data.product ? {
            value: data.product.id,
            label: `${data.product.sku} - ${data.product.name}`
          } : undefined,
          inventory_type_id: data.inventory_type?.id,
          mechanism: data.mechanism,
          quantity: data.quantity,
          lookback_days: data.lookback_days,
          target_coverage_days: data.target_coverage_days,
          expiry_days: data.expiry_days,
        });
      } else {
        message.error(response.message || t('common.error.load_failed'));
      }
    } catch (error) {
      console.error('Failed to load inventory monitor:', error);
      message.error(t('common.error.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 將表單 values 轉成 API payload
  const buildData = (values: Record<string, any>) => ({
    // 新增模式：form 內 merchant_id 由 wrapper 注入或下拉選擇；
    // 編輯模式：form 內 merchant_id 是 record 的 merchant，BE update 會忽略（except :merchant_id）。
    merchant_id: values.merchant_id,
    product_id: values.product_id?.value || values.product_id,
    inventory_type_id: values.inventory_type_id,
    mechanism: values.mechanism,
    quantity: values.quantity || null,
    lookback_days: values.lookback_days || null,
    target_coverage_days: values.target_coverage_days || null,
    expiry_days: values.expiry_days || null,
  });

  // 核心提交邏輯：支援 service 或 legacy 回呼
  const submitValues = async (data: Record<string, unknown>) => {
    if (inventoryMonitorService) {
      return isEditMode
        ? await inventoryMonitorService.update(inventoryMonitorId!, data)
        : await inventoryMonitorService.create(data);
    }
    const legacyPayload = { inventory_monitor: data };
    return isEditMode
      ? await updateInventoryMonitor!(inventoryMonitorId!, legacyPayload)
      : await createInventoryMonitor!(legacyPayload);
  };

  // 套用後端欄位錯誤
  const applyFieldErrors = (response: { errors?: Record<string, unknown> }) => {
    if (response.errors) {
      const fieldErrors: Record<string, { errors: string[] }> = {};
      Object.keys(response.errors).forEach(key => {
        const raw = (response.errors as Record<string, unknown>)[key];
        fieldErrors[key] = {
          errors: Array.isArray(raw) ? (raw as string[]) : [String(raw)]
        };
      });
      form.setFields(Object.keys(fieldErrors).map(name => ({
        name,
        errors: fieldErrors[name].errors
      })));
    }
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values: Record<string, any>) => {
    setSubmitting(true);
    try {
      const data = buildData(values);
      const response = await submitValues(data);

      if (response.success) {
        message.success(t(isEditMode ? 'common.message.update_success' : 'common.message.create_success'));
        const recordId = response.data?.id || inventoryMonitorId;
        navigate(`/inventory_monitors/${recordId}`, { replace: true });
      } else {
        applyFieldErrors(response);
        message.error(response.message || t('common.error.save_failed'));
      }
    } catch (error) {
      console.error('Failed to save inventory monitor:', error);
      message.error(t('common.error.save_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const data = buildData(values);
        const response = await submitValues(data);

        if (response.success) {
          message.success(t('common.message.create_success'));
          form.resetFields();
          // 重新套用 wrapper 注入的 merchantIdForCreate（避免被 resetFields 清掉）
          if (merchantIdForCreate) {
            form.setFieldsValue({ merchant_id: merchantIdForCreate });
          }
        } else {
          applyFieldErrors(response);
          message.error(response.message || t('common.error.save_failed'));
        }
      } catch (error) {
        console.error('Failed to save inventory monitor:', error);
        message.error(t('common.error.save_failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  const renderMechanismDescription = () => {
    if (!mechanism) return null;

    const descriptions: Record<string, string> = {
      quantity: t('shared.inventory_monitors.form.mechanisms.quantity_description'),
      turnover_days: t('shared.inventory_monitors.form.mechanisms.turnover_days_description'),
      stalled_days: t('shared.inventory_monitors.form.mechanisms.stalled_days_description'),
    };

    return (
      <Alert
        message={descriptions[mechanism]}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  };

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  // Render-gate: sync 期間（loading 抓 record；merchantDataLoading 切 context 到
  // record 的 merchant）整段 form 不能 mount，避免 product / inventory_type 下拉
  // 先用 header merchant 的清單 render。
  if (loading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        basePath={headerBasePath}
        items={['stocks', 'stocks.trackers', { label: isEditMode ? t('common.button.edit') : t('common.button.create') }]}
      />
      <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ maxWidth: 800 }}
        >
          {showMerchantSelect ? (
            <Form.Item
              label={t('shared.inventory_monitors.list.filters.merchant')}
              name="merchant_id"
              rules={[{ required: true, message: t('common.validations.required') }]}
            >
              <Select
                placeholder={t('common.placeholder.select_merchant')}
                onChange={() => form.setFieldsValue({ product_id: undefined, inventory_type_id: undefined })}
              >
                {merchantOptions.map(merchant => (
                  <Option key={merchant.value} value={merchant.value}>
                    {merchant.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.Item name="merchant_id" hidden><Input /></Form.Item>
          )}

          <Form.Item
            label={t('shared.inventory_monitors.form.fields.product')}
            name="product_id"
            rules={[{ required: true, message: t('shared.inventory_monitors.form.validations.product_required') }]}
          >
            <ProductSelect
              productService={productService as any}
              placeholder={t('shared.inventory_monitors.form.fields.product_placeholder')}
              labelInValue
              merchantId={merchantId}
            />
          </Form.Item>

          <Form.Item
            label={t('shared.inventory_monitors.form.fields.inventory_type')}
            name="inventory_type_id"
            rules={[{ required: true, message: t('shared.inventory_monitors.form.validations.inventory_type_required') }]}
          >
            <Select
              placeholder={t('shared.inventory_monitors.form.fields.inventory_type_placeholder')}
            >
              {inventoryTypes
                // wrapper 已依 merchant 過濾，這裡 defense-in-depth：若 type 帶
                // merchant_id（merchant_inventory_types 結構）再做一次過濾。
                .filter(type => !merchantId || !type.merchant_id || type.merchant_id === merchantId)
                .map(type => {
                  const inventoryTypeId = type.inventory_type_id ?? type.inventory_type?.id ?? type.id;
                  const name = type.inventory_type?.name ?? type.name;
                  const code = type.inventory_type?.code ?? type.code;
                  return (
                    <Option key={type.id ?? inventoryTypeId} value={inventoryTypeId}>
                      {name} ({code})
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('shared.inventory_monitors.form.fields.mechanism')}
            name="mechanism"
            rules={[{ required: true, message: t('shared.inventory_monitors.form.validations.mechanism_required') }]}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="quantity">
                  {t('shared.inventory_monitors.form.mechanisms.quantity')}
                </Radio>
                <Radio value="turnover_days">
                  {t('shared.inventory_monitors.form.mechanisms.turnover_days')}
                </Radio>
                <Radio value="stalled_days">
                  {t('shared.inventory_monitors.form.mechanisms.stalled_days')}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {renderMechanismDescription()}

          {mechanism === 'quantity' && (
            <>
              <Form.Item
                label={t('shared.inventory_monitors.form.mechanism_hints.quantity')}
                name="quantity"
                rules={[{ required: true, message: t('shared.inventory_monitors.form.validations.quantity_required') }]}
              >
                <InputNumber
                  placeholder={t('shared.inventory_monitors.form.fields.quantity_placeholder')}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>

              <Form.Item
                label={t('shared.inventory_monitors.form.fields.expiry_days')}
                name="expiry_days"
                help={t('shared.inventory_monitors.form.fields.expiry_days_help')}
              >
                <InputNumber
                  placeholder={t('shared.inventory_monitors.form.fields.expiry_days_placeholder')}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </>
          )}

          {mechanism === 'turnover_days' && (
            <>
              <Form.Item
                label={t('shared.inventory_monitors.form.mechanism_hints.turnover_lookback_days')}
                name="lookback_days"
                rules={[{ required: true, message: t('shared.inventory_monitors.form.validations.lookback_days_required') }]}
              >
                <InputNumber
                  placeholder={t('shared.inventory_monitors.form.fields.lookback_days_placeholder')}
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>

              <Form.Item
                label={t('shared.inventory_monitors.form.mechanism_hints.target_coverage_days')}
                name="target_coverage_days"
                rules={[{ required: true, message: t('shared.inventory_monitors.form.validations.target_coverage_days_required') }]}
              >
                <InputNumber
                  placeholder={t('shared.inventory_monitors.form.fields.target_coverage_days_placeholder')}
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>

              <Form.Item
                label={t('shared.inventory_monitors.form.fields.expiry_days')}
                name="expiry_days"
                help={t('shared.inventory_monitors.form.fields.expiry_days_help')}
              >
                <InputNumber
                  placeholder={t('shared.inventory_monitors.form.fields.expiry_days_placeholder')}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </>
          )}

          {mechanism === 'stalled_days' && (
            <>
              <Form.Item
                label={t('shared.inventory_monitors.form.mechanism_hints.stalled_lookback_days')}
                name="lookback_days"
                rules={[{ required: true, message: t('shared.inventory_monitors.form.validations.lookback_days_required') }]}
              >
                <InputNumber
                  placeholder={t('shared.inventory_monitors.form.fields.lookback_days_placeholder')}
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>

              <Form.Item
                label={t('shared.inventory_monitors.form.mechanism_hints.stalled_days_quantity')}
                name="quantity"
                rules={[{ required: true, message: t('shared.inventory_monitors.form.validations.quantity_required') }]}
              >
                <InputNumber
                  placeholder={t('shared.inventory_monitors.form.fields.quantity_placeholder')}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </>
          )}

          {!isEditMode && (
            <Alert
              message={t('shared.inventory_monitors.form.notes.duplicate_update')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <FloatingActionBarSpacer />
        </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={submitting}
        disabled={savingAndAdding}
        extraButtons={!isEditMode ? (
          <Button
            loading={savingAndAdding}
            disabled={submitting}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
      />
    </div>
  );
}
